'use client';

import { useInView } from 'motion/react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useId, useRef, useState } from 'react';
import { TrackedAnchor } from '@/components/ancher/tracked-anchor';
import { TrackedButton } from '@/components/button/tracked-button';
import { ChevronIcon } from '@/components/icons/chevron-icons';
import { TRACKED_EVENT_POSITION } from '@/constants/gtag';
import { useLiteMode } from '@/hooks/useLiteMode.hook';
import { alwaysBreakRichTextTags } from './main-responsive';
import { cn } from '@lib/utils';
import ScrollHold from './_components/scroll-hold';

const PARTNERS = [
  'countries',
  'providers',
  'patients',
  'pharma',
  'publications',
] as const;

export default function PartnersSection() {
  const t = useTranslations('Main.partners');
  const id = useId();
  const ref = useRef<HTMLUListElement>(null);
  // 브라우저 기본 lazy 임계값은 한 화면도 안 된다(실측 Safari 719px·Chrome 1150px).
  // 이 섹션은 문서 11,200px 지점이라 도착했을 때 이미지가 아직 비어 있었다.
  // 끝까지 안 내려가면 여전히 받지 않는다.
  const nearViewport = useInView(ref, { margin: '3000px', once: true });
  const isLiteMode = useLiteMode();
  const [edges, setEdges] = useState({ start: true, end: false });
  useEffect(() => {
    const list = ref.current;
    if (!list) return;
    const measure = () => {
      const start = list.scrollLeft <= 1;
      const end = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
      setEdges(previous =>
        previous.start === start && previous.end === end
          ? previous
          : { start, end },
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    list.addEventListener('scroll', measure, { passive: true });
    measure();
    return () => {
      observer.disconnect();
      list.removeEventListener('scroll', measure);
    };
  }, []);
  const move = (direction: number) => {
    const list = ref.current;
    const card = list?.firstElementChild;
    if (!list || !card) return;
    const distance =
      card.getBoundingClientRect().width +
      parseFloat(getComputedStyle(list).columnGap);
    list.scrollBy({
      left: direction * distance,
      behavior: isLiteMode ? 'instant' : 'smooth',
    });
  };
  return (
    <ScrollHold className="bg-white">
      <section
        data-main-partners
        aria-labelledby={`${id}-title`}
        className="overflow-hidden bg-white px-5 py-20 xl:px-6 xl:py-[180px]"
      >
        <div className="mx-auto flex w-full max-w-[1260px] flex-col gap-10 xl:gap-20">
          <h2
            id={`${id}-title`}
            className="text-center text-[24px] font-semibold leading-8 tracking-[-0.464px] text-[#040435] xl:text-[48px] xl:leading-[56px] xl:tracking-[-1.072px]"
          >
            {t.rich('title', alwaysBreakRichTextTags)}
          </h2>
          <ul
            ref={ref}
            id={`${id}-cards`}
            aria-label={t('listLabel')}
            className="-mx-3 -my-4 flex snap-x snap-mandatory gap-8 overflow-x-auto py-4 pl-3 pr-[max(32px,calc((100vw-1260px)/2))] [scroll-padding-left:12px] [scrollbar-width:none] [width:calc(100vw-max(24px,(100vw-1260px)/2)+12px)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#040435] [&::-webkit-scrollbar]:hidden"
          >
            {PARTNERS.map(key => (
              // 모바일도 데스크톱과 같은 카드(300×454, hover·포커스 시 315×472.81)를 쓴다
              <li
                key={key}
                className="relative h-[454px] w-[300px] shrink-0 snap-start"
              >
                <TrackedAnchor
                  href={t(`${key}.href`)}
                  target="_blank"
                  label={`main-partners_${key}`}
                  position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
                  className={cn(
                    'group/card absolute left-1/2 top-1/2 flex h-[454px] w-[300px] -translate-x-1/2 -translate-y-1/2 flex-col justify-between rounded-[20px] bg-[linear-gradient(153.18deg,rgba(104,169,255,0.2)_4.38%,rgba(236,248,255,0.2)_100.45%)] p-3',
                    'transition-[width,height,padding,border-radius,box-shadow] duration-200 ease-out motion-reduce:transition-none',
                    '[@media(hover:hover)]:hover:z-[1] [@media(hover:hover)]:hover:h-[472.81px] [@media(hover:hover)]:hover:w-[315px] [@media(hover:hover)]:hover:rounded-[21px] [@media(hover:hover)]:hover:bg-none [@media(hover:hover)]:hover:p-[12.6px] [@media(hover:hover)]:hover:shadow-[0_4px_10px_rgba(0,0,0,0.04)]',
                    'focus-visible:z-[1] focus-visible:h-[472.81px] focus-visible:w-[315px] focus-visible:rounded-[21px] focus-visible:bg-none focus-visible:p-[12.6px] focus-visible:shadow-[0_4px_10px_rgba(0,0,0,0.04)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#040435]',
                    "before:pointer-events-none before:absolute before:inset-0 before:-z-[1] before:rounded-[inherit] before:opacity-0 before:shadow-[inset_1px_1px_1px_rgba(255,255,255,0.8),inset_-1px_-1px_2px_rgba(255,255,255,0.3)] before:backdrop-blur-[24px] before:content-['']",
                    'before:transition-opacity before:duration-200 before:ease-out focus-visible:before:opacity-100 motion-reduce:before:transition-none [@media(hover:hover)]:hover:before:opacity-100',
                  )}
                >
                  {/* 시안: 일러스트가 카드 전체를 덮고, 아래쪽은 흰 페이드로 글자 영역을 받친다 */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
                  >
                    <Image
                      src={`/images/main/partners/${key}.webp`}
                      alt=""
                      fill
                      // 카드 폭은 고정이다 — hover·포커스로 커졌을 때가 315px.
                      sizes="315px"
                      loading={nearViewport ? 'eager' : 'lazy'}
                      className="object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-[rgba(255,255,255,0)] from-[21.255%] to-[rgba(243,250,255,0.9)] to-[57.645%]" />
                  </div>
                  <div className="relative h-[220px] w-full shrink-0 transition-[height] duration-200 ease-out group-focus-visible/card:h-[231px] motion-reduce:transition-none [@media(hover:hover)]:group-hover/card:h-[231px]" />
                  <div className="relative flex w-full flex-1 flex-col gap-4 px-5 pb-5 pt-2.5 transition-[padding,gap] duration-200 ease-out group-focus-visible/card:gap-[18px] group-focus-visible/card:px-[21px] group-focus-visible/card:pb-[21px] group-focus-visible/card:pt-[11px] motion-reduce:transition-none [@media(hover:hover)]:group-hover/card:gap-[18px] [@media(hover:hover)]:group-hover/card:px-[21px] [@media(hover:hover)]:group-hover/card:pb-[21px] [@media(hover:hover)]:group-hover/card:pt-[11px]">
                    <div className="flex flex-col gap-1.5">
                      <p className="text-[48px] font-semibold leading-[56px] tracking-[-1.072px] text-[#040435] transition-[font-size] duration-200 ease-out group-focus-visible/card:text-[50px] motion-reduce:transition-none [@media(hover:hover)]:group-hover/card:text-[50px]">
                        {t(`${key}.value`)}
                      </p>
                      <p className="text-[18px] font-medium leading-7 tracking-[-0.256px] text-3b-text-primary transition-[font-size] duration-200 ease-out group-focus-visible/card:text-[20px] motion-reduce:transition-none [@media(hover:hover)]:group-hover/card:text-[20px]">
                        {t.rich(`${key}.description`, alwaysBreakRichTextTags)}
                      </p>
                    </div>
                    {/* hover·포커스 시 가운데 기준으로 글자 영역 밖까지 커진다(시안 258.57×48.61) */}
                    <span className="mt-auto flex min-h-[46px] w-full items-center justify-center self-center rounded-full border border-solid border-[#e4e6eb] bg-white px-4 py-1.5 text-center text-[16px] font-medium leading-8 tracking-[-0.464px] text-[#6c7380] transition-[color,background-color,border-color,width,min-height,padding,font-size] duration-200 group-focus-visible/card:min-h-[48.61px] group-focus-visible/card:w-[258.57px] group-focus-visible/card:border-transparent group-focus-visible/card:bg-[#040435] group-focus-visible/card:px-[16.7px] group-focus-visible/card:py-[6.26px] group-focus-visible/card:text-[16.7px] group-focus-visible/card:text-[#e4e6eb] group-active/card:border-transparent group-active/card:bg-[#040435] group-active/card:text-[#e4e6eb] motion-reduce:transition-none [@media(hover:hover)]:group-hover/card:min-h-[48.61px] [@media(hover:hover)]:group-hover/card:w-[258.57px] [@media(hover:hover)]:group-hover/card:border-transparent [@media(hover:hover)]:group-hover/card:bg-[#040435] [@media(hover:hover)]:group-hover/card:px-[16.7px] [@media(hover:hover)]:group-hover/card:py-[6.26px] [@media(hover:hover)]:group-hover/card:text-[16.7px] [@media(hover:hover)]:group-hover/card:text-[#e4e6eb]">
                      {t('viewDetails')}
                    </span>
                  </div>
                </TrackedAnchor>
              </li>
            ))}
          </ul>
          <div className="flex justify-center gap-4 xl:gap-[23px]">
            {(['prev', 'next'] as const).map(direction => (
              <TrackedButton
                key={direction}
                type="button"
                aria-label={t(direction)}
                aria-controls={`${id}-cards`}
                disabled={direction === 'prev' ? edges.start : edges.end}
                onClick={() => move(direction === 'prev' ? -1 : 1)}
                label={`main-partners_${direction}`}
                position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
                className="grid size-12 place-items-center rounded-[50%] bg-3b-background-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#040435] enabled:hover:bg-[#e1e5ec] disabled:cursor-default disabled:opacity-40 xl:size-[72px]"
              >
                <ChevronIcon
                  direction={direction === 'prev' ? 'left' : 'right'}
                  className="size-6 text-[#6C7380] xl:size-9"
                />
              </TrackedButton>
            ))}
          </div>
        </div>
      </section>
    </ScrollHold>
  );
}
